<template>
  <HermesDrawer
    :model-value="modelValue"
    :title="isNew ? 'New user' : 'Edit user'"
    :close-on-scrim="!dirty"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div>
        <h3 class="h-ued__title">{{ isNew ? 'New user' : (user?.name || 'Edit user') }}</h3>
        <div class="h-ued__sub">{{ isNew ? 'Someone who can sign in to this Hermes admin.' : user?.email }}</div>
      </div>
    </template>

    <div class="h-ued">
      <div v-if="user?.is_self" class="h-ued__callout">
        <b>This is you.</b> Your own roles, access and password are changed from
        <a href="/admin/settings">My settings</a>, not here.
      </div>
      <div v-else-if="user?.is_owner" class="h-ued__callout">
        <b>The platform owner account.</b> It cannot be deactivated, moved or have its roles changed.
      </div>

      <!-- ===== Details ===== -->
      <div class="h-ued__grid2">
        <div class="h-ued__field">
          <label class="h-ued__label" for="h-ued-first">First name <span class="h-ued__req">*</span></label>
          <input id="h-ued-first" v-model.trim="form.first_name" class="h-ued__input" type="text" autocomplete="off" maxlength="255" />
          <p v-if="fieldError('first_name')" class="h-ued__err">{{ fieldError('first_name') }}</p>
        </div>
        <div class="h-ued__field">
          <label class="h-ued__label" for="h-ued-last">Last name <span class="h-ued__req">*</span></label>
          <input id="h-ued-last" v-model.trim="form.last_name" class="h-ued__input" type="text" autocomplete="off" maxlength="255" />
          <p v-if="fieldError('last_name')" class="h-ued__err">{{ fieldError('last_name') }}</p>
        </div>
      </div>

      <div class="h-ued__field">
        <label class="h-ued__label" for="h-ued-email">Email <span class="h-ued__req">*</span></label>
        <input id="h-ued-email" v-model.trim="form.email" class="h-ued__input" type="email" autocomplete="off" maxlength="255" />
        <p class="h-ued__hint">They sign in with this address.</p>
        <p v-if="fieldError('email')" class="h-ued__err">{{ fieldError('email') }}</p>
      </div>

      <!-- ===== Password ===== -->
      <div v-if="isNew || canResetPassword" class="h-ued__field">
        <label class="h-ued__label" for="h-ued-pw">
          {{ isNew ? 'Password' : 'Set a new password' }}
          <span v-if="isNew" class="h-ued__req">*</span>
        </label>
        <input
          id="h-ued-pw"
          v-model="form.password"
          class="h-ued__input"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          maxlength="200"
        />
        <p class="h-ued__hint">
          At least {{ minLength }} characters.
          <template v-if="!isNew">Leave empty to keep the current one. Setting one signs them out everywhere.</template>
          <button type="button" class="h-ued__link" @click="showPassword = !showPassword">{{ showPassword ? 'Hide' : 'Show' }}</button>
        </p>
        <p v-if="fieldError('password')" class="h-ued__err">{{ fieldError('password') }}</p>
      </div>

      <!-- ===== Placement (platform operators only) ===== -->
      <template v-if="meta.platform">
        <div class="h-ued__field">
          <label class="h-ued__label" for="h-ued-vendor">Vendor <span v-if="!form.is_platform_admin" class="h-ued__req">*</span></label>
          <select id="h-ued-vendor" v-model="form.vendor_id" class="h-ued__input" :disabled="accessLocked">
            <option :value="null">{{ form.is_platform_admin ? 'No vendor' : 'Choose a vendor…' }}</option>
            <option v-for="v in meta.vendors" :key="v.id" :value="v.id">{{ v.name }}</option>
          </select>
          <p class="h-ued__hint">The vendor whose data this person works with.</p>
          <p v-if="fieldError('vendor_id')" class="h-ued__err">{{ fieldError('vendor_id') }}</p>
        </div>

        <label class="h-ued__switch" :class="{ 'is-disabled': accessLocked }">
          <input type="checkbox" v-model="form.is_platform_admin" :disabled="accessLocked" />
          <span class="h-ued__track"></span>
          <span class="h-ued__swlbl">Platform operator</span>
        </label>
        <p class="h-ued__hint h-ued__hint--tight">
          Sees every vendor's data and can act as any vendor. What they may DO still comes from their roles.
        </p>
      </template>

      <!-- ===== Roles ===== -->
      <div class="h-ued__field">
        <label class="h-ued__label">Roles</label>
        <div v-if="!meta.roles.length" class="h-ued__hint">No roles to show.</div>
        <div v-else class="h-ued__roles">
          <label
            v-for="r in meta.roles"
            :key="r.id"
            class="h-ued__role"
            :class="{ on: form.role_ids.includes(r.id), 'is-disabled': roleDisabled(r) }"
          >
            <input type="checkbox" :value="r.id" v-model="form.role_ids" :disabled="roleDisabled(r)" />
            <span>{{ r.name }}</span>
            <small v-if="!r.assignable">not yours to give</small>
          </label>
        </div>
        <p v-if="fieldError('role_ids')" class="h-ued__err">{{ fieldError('role_ids') }}</p>
      </div>

      <!-- ===== Active ===== -->
      <label class="h-ued__switch" :class="{ 'is-disabled': accessLocked }">
        <input type="checkbox" v-model="form.active" :disabled="accessLocked" />
        <span class="h-ued__track"></span>
        <span class="h-ued__swlbl">Active</span>
      </label>
      <p class="h-ued__hint h-ued__hint--tight">
        An inactive user cannot sign in. Switching someone off signs them out everywhere at once.
      </p>

      <p v-if="error" class="h-ued__saveerr">{{ error }}</p>
      <p v-if="notice" class="h-ued__saveok">{{ notice }}</p>
    </div>

    <template #footer>
      <button type="button" class="h-btn h-btn--ghost" @click="$emit('update:modelValue', false)">Cancel</button>
      <button type="button" class="h-btn h-btn--primary" :disabled="saving || !canSubmit" @click="submit">
        <span v-if="saving" class="h-ued__spin"></span>
        {{ saving ? 'Saving…' : (isNew ? 'Create user' : 'Save changes') }}
      </button>
    </template>
  </HermesDrawer>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesUserEditorDrawer — create or edit one user. Opened by HermesUsersList.
//
// One Save covers up to four API calls, each behind its own permission, and only
// the ones whose part of the form changed are made:
//   details  PATCH /admin/users/{id}            (User::UPDATE)
//   roles    PUT   /admin/users/{id}/roles      (Role::SET)
//   active   PUT   /admin/users/{id}/active     (User::CHANGE_ACTIVE_STATE)
//   password POST  /admin/users/{id}/password   (User::MANAGE)
// A new user is one call: POST /admin/users.
//
// The screen disables what the API would refuse (your own access, the platform
// owner's, roles that are not yours to give), but the API is what enforces it.
export default {
  name: 'HermesUserEditorDrawer',
  props: {
    modelValue: { type: Boolean, default: false },
    apiBase: { type: String, default: '/api/v1' },
    // The row being edited, or null for a new user.
    user: { type: Object, default: null },
    meta: { type: Object, default: () => ({ platform: false, roles: [], vendors: [], password_min_length: 12 }) },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      api: useApi(),
      form: this.formFrom(null),
      original: this.formFrom(null),
      saving: false,
      error: '',
      notice: '',
      errorField: '',
      showPassword: false,
    };
  },
  computed: {
    isNew() {
      return !this.user;
    },
    minLength() {
      return this.meta.password_min_length || 12;
    },
    // Your own account and the platform owner's: access cannot be changed here.
    accessLocked() {
      return !this.isNew && this.user.can_change_access === false;
    },
    canResetPassword() {
      return !this.isNew && !this.user.is_self;
    },
    detailsChanged() {
      return ['first_name', 'last_name', 'email', 'vendor_id', 'is_platform_admin']
        .some((k) => this.form[k] !== this.original[k]);
    },
    rolesChanged() {
      const a = [...this.form.role_ids].sort((x, y) => x - y).join(',');
      const b = [...this.original.role_ids].sort((x, y) => x - y).join(',');
      return a !== b;
    },
    activeChanged() {
      return this.form.active !== this.original.active;
    },
    dirty() {
      return this.detailsChanged || this.rolesChanged || this.activeChanged || !!this.form.password;
    },
    canSubmit() {
      if (!this.form.first_name || !this.form.last_name) return false;
      if (!/.+@.+\..+/.test(this.form.email)) return false;
      if (this.meta.platform && !this.form.is_platform_admin && !this.form.vendor_id) return false;
      if (this.isNew) return this.form.password.length >= this.minLength;
      if (this.form.password && this.form.password.length < this.minLength) return false;
      return this.dirty;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.reset();
    },
  },
  methods: {
    formFrom(user) {
      return {
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || '',
        vendor_id: user ? (user.vendor_id ?? null) : (this.meta?.platform ? null : (this.meta?.vendor_id ?? null)),
        is_platform_admin: !!user?.is_platform_admin,
        active: user ? !!user.active : true,
        role_ids: (user?.roles || []).map((r) => r.id),
        password: '',
      };
    },
    reset() {
      this.form = this.formFrom(this.user);
      this.original = this.formFrom(this.user);
      this.saving = false;
      this.error = '';
      this.notice = '';
      this.errorField = '';
      this.showPassword = false;
    },
    roleDisabled(role) {
      if (this.accessLocked) return true;
      // A role that is not yours to give can be neither added nor removed.
      return !role.assignable;
    },
    fieldError(field) {
      return this.errorField === field ? this.error : '';
    },
    async submit() {
      if (!this.canSubmit || this.saving) return;
      this.saving = true;
      this.error = '';
      this.notice = '';
      this.errorField = '';

      try {
        if (this.isNew) {
          const payload = {
            first_name: this.form.first_name,
            last_name: this.form.last_name,
            email: this.form.email,
            password: this.form.password,
            active: this.form.active,
            role_ids: this.form.role_ids,
          };
          if (this.meta.platform) {
            payload.vendor_id = this.form.vendor_id;
            payload.is_platform_admin = this.form.is_platform_admin;
          }
          await this.api.post(`${this.apiBase}/admin/users`, payload);
        } else {
          const base = `${this.apiBase}/admin/users/${this.user.id}`;

          if (this.detailsChanged) {
            const payload = {
              first_name: this.form.first_name,
              last_name: this.form.last_name,
              email: this.form.email,
            };
            if (this.meta.platform && !this.accessLocked) {
              payload.vendor_id = this.form.vendor_id;
              payload.is_platform_admin = this.form.is_platform_admin;
            }
            await this.api.patch(base, payload);
            this.original = { ...this.original, ...payload };
          }
          if (this.rolesChanged) {
            await this.api.put(`${base}/roles`, { role_ids: this.form.role_ids });
            this.original = { ...this.original, role_ids: [...this.form.role_ids] };
          }
          if (this.activeChanged) {
            await this.api.put(`${base}/active`, { active: this.form.active });
            this.original = { ...this.original, active: this.form.active };
          }
          if (this.form.password) {
            await this.api.post(`${base}/password`, { password: this.form.password });
            this.form.password = '';
          }
        }

        this.$emit('saved');
        this.$emit('update:modelValue', false);
      } catch (e) {
        this.error = this.errMsg(e);
        this.errorField = e?.response?.data?.field || '';
        // Parts saved before the failure stay saved: refresh the list behind.
        this.$emit('saved');
      } finally {
        this.saving = false;
      }
    },
    errMsg(e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message;
      if (serverMsg) return serverMsg;
      if (status === 403) return 'You do not have permission to do that.';
      return `Could not save${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
    },
  },
};
</script>

<style scoped>
.h-ued { font-family: var(--h-font-body, system-ui); }
.h-ued__title { margin: 0; font-size: 15px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-ued__sub { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin-top: 2px; }

.h-ued__field { margin-bottom: 16px; }
.h-ued__label { display: block; font-size: 13px; font-weight: 500; color: var(--h-text-primary, #0F172A); margin-bottom: 6px; }
.h-ued__req { color: var(--h-danger, #EF4444); margin-left: 2px; }
.h-ued__hint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 6px 0 0; line-height: 1.5; }
.h-ued__hint--tight { margin: -4px 0 16px; }
.h-ued__err { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin: 6px 0 0; }
.h-ued__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.h-ued__link { border: none; background: none; padding: 0; margin-left: 6px; font: inherit; font-size: 12px; color: var(--h-accent-dark, #0369A1); cursor: pointer; text-decoration: underline; }

.h-ued__input { width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A); border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box; }
.h-ued__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-ued__input:disabled { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #64748B); cursor: not-allowed; }

.h-ued__callout { font-size: 12.5px; line-height: 1.5; color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); padding: 11px 13px; margin-bottom: 16px; }
.h-ued__callout b { color: var(--h-text-primary, #0F172A); }
.h-ued__callout a { color: var(--h-accent-dark, #0369A1); }

.h-ued__roles { display: flex; flex-direction: column; gap: 6px; }
.h-ued__role { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); cursor: pointer; font-size: 13.5px; color: var(--h-text-primary, #0F172A); }
.h-ued__role.on { border-color: var(--h-accent, #0EA5E9); background: var(--h-accent-light, #E0F2FE); }
.h-ued__role.is-disabled { cursor: not-allowed; opacity: 0.65; }
.h-ued__role small { margin-left: auto; font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); }

.h-ued__switch { position: relative; display: flex; align-items: center; gap: 12px; cursor: pointer; margin-bottom: 8px; }
.h-ued__switch.is-disabled { cursor: not-allowed; opacity: 0.6; }
.h-ued__switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.h-ued__track { width: 38px; height: 22px; background: var(--h-border-strong, #CBD5E1); border-radius: 999px; position: relative; transition: background 0.15s; flex: none; }
.h-ued__track::after { content: ''; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform 0.15s; }
.h-ued__switch input:checked + .h-ued__track { background: var(--h-accent, #0EA5E9); }
.h-ued__switch input:checked + .h-ued__track::after { transform: translateX(16px); }
.h-ued__switch input:focus-visible + .h-ued__track { box-shadow: var(--h-focus-ring); }
.h-ued__swlbl { font-size: 13.5px; color: var(--h-text-primary, #0F172A); }

.h-ued__saveerr { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 14px; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); }
.h-ued__saveok { font-size: 12.5px; color: var(--h-success-text, #047857); margin-top: 14px; padding: 10px 12px; background: var(--h-success-bg, #ECFDF5); border-radius: var(--h-radius, 6px); }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--ghost { background: transparent; color: var(--h-text-secondary, #64748B); }
.h-btn--ghost:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary, #0F172A); }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.h-ued__spin { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: h-ued-spin 0.7s linear infinite; }
@keyframes h-ued-spin { to { transform: rotate(360deg); } }
</style>
